import TextButton from 'LuxKit/Button/TextButton'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyText from 'LuxKit/Typography/BodyText'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import LoyaltyBenefitItemTile from 'loyalty/components/LoyaltyBenefitItemTile'
import LoyaltyBenefitCompactRow from 'loyalty/components/LoyaltyBenefitCompactRow'
import LoyaltyEsimDetailsModal from 'loyalty/components/LoyaltyEsimDetailsModal'
import LoyaltyEsimAdditionalDataRow from 'loyalty/components/LoyaltyEsimAdditionalDataRow'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import getCanShowOrderEsimUpsellRow from 'checkout/selectors/loyalty/esim/getCanShowOrderEsimUpsellRow'
import Group from 'components/utils/Group'
import { useLoyaltyEsimImage } from 'loyalty/constants/loyaltyEsimConstants'
import {
  formatEsimInternationalDataRowHeading,
  getEsimBenefitTileMessages,
} from 'loyalty/utils/loyaltyEsimLabels'
import useLoyaltyOrderEsimBenefitView from 'loyalty/hooks/useLoyaltyOrderViewEsimData'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import useEsimUpsellCatalog from 'loyalty/hooks/useEsimUpsellCatalog'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import LoyaltyTierMessageBanner from './LoyaltyTierMessageBanner'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'

interface Props {
  order: App.Order
  layout?: 'tile' | 'row'
}

function LoyaltyOrderViewBenefitsEsimItem({ order, layout = 'tile' }: Props) {
  const intl = useIntl()
  const esimBenefitView = useLoyaltyOrderEsimBenefitView(order.id)
  const rebrandSuccessDarkColour = useRebrandSuccessDarkColour()
  const showModal = useContext(ModalContext)
  const esimImage = useLoyaltyEsimImage()

  const {
    isEligible: canShowUpsellRow,
    countryCode: upsellCountryCode,
  } = useAppSelector(getCanShowOrderEsimUpsellRow)

  const {
    catalog: upsellCatalog,
    canShowUpsellCatalog,
  } = useEsimUpsellCatalog({
    countryCode: upsellCountryCode ?? undefined,
    orderId: order.id,
    currentPackageTicketId: esimBenefitView?.currentPackageTicketId,
    enabled: canShowUpsellRow,
  })

  const onActionClick = useCallback(() => {
    showModal(<LoyaltyEsimDetailsModal orderId={order.id} />)
  }, [showModal, order.id])

  if (!esimBenefitView || esimBenefitView.tripEnded) return null

  const {
    includedBenefit,
    purchasedUpsell,
    currentPackageTicketId,
    isWithinRedemptionWindow,
  } = esimBenefitView

  const {
    heading,
    description,
    addedLabel,
    detailsLabel,
  } = getEsimBenefitTileMessages(intl, {
    includedGb: includedBenefit.includedGb,
    selectedPackageTicketId: purchasedUpsell?.ticketId,
  })

  const availabilityNote = intl.formatMessage({
    id: 'bR0Po-PE5ozKbAcLohD7',
    defaultMessage: 'Activates 7 days before departure',
    description: 'eSIM availability note showing activation timeline',
  })
  const redeemLabel = intl.formatMessage({
    id: 'VHz2lQb8PWn8-_kQU5_-',
    defaultMessage: 'Redeem',
    description: 'Button to open eSIM details/redeem modal on order and booking confirmation',
  })
  const complimentaryGbThisTripLabel = intl.formatMessage(
    {
      id: '0E7T9VS_Hx1WOA6xxF-X',
      defaultMessage: '{amount}GB for this trip',
      description: 'Label showing eSIM data allowance for this trip for account benefit tile',
    },
    { amount: includedBenefit.includedGb },
  )
  const historicalTierLabel = intl.formatMessage(
    {
      id: 'aM4jB2hzoUP2a3KXy14H',
      defaultMessage: 'Added when you were {tierName}',
      description: 'Pill copy on an order eSIM benefit when the included allowance came from the member’s older tier',
    },
    { tierName: getLoyaltyTierDisplayName(includedBenefit.tier) },
  )

  const actionButton = (
    <TextButton kind="secondary" onClick={onActionClick}>
      {isWithinRedemptionWindow ? redeemLabel : detailsLabel}
    </TextButton>
  )

  if (layout === 'row') {
    const rowHeading = formatEsimInternationalDataRowHeading(intl, currentPackageTicketId)
    const activationNote = intl.formatMessage({
      id: 'z3xS9H2PqLS5EP2t21gK',
      defaultMessage: 'Activate 7 days before departure',
      description: 'eSIM activation timing note on the upcoming escapes card',
    })
    const viewLabel = intl.formatMessage({
      id: '2PotyrTgljknniT_VfLn',
      defaultMessage: 'View',
      description: 'Button to view eSIM benefit details on the upcoming escapes card',
    })

    return (
      <LoyaltyBenefitCompactRow
        benefitType="esim_package"
        heading={rowHeading}
        status={!isWithinRedemptionWindow && (
          <BodyText variant="medium" colour={rebrandSuccessDarkColour} weight="bold">
            {activationNote}
          </BodyText>
        )}
        action={
          <TextButton kind="secondary" variant="default" size="small" shape="round" onClick={onActionClick}>
            {isWithinRedemptionWindow ? redeemLabel : viewLabel}
          </TextButton>
        }
      />
    )
  }

  return (
    <LoyaltyBenefitItemTile
      heading={heading}
      labels={
        <>
          {includedBenefit.isHistoricalTier && (
            <LoyaltyTierLabel kind="primary" tier={includedBenefit.tier}>
              {complimentaryGbThisTripLabel}
            </LoyaltyTierLabel>
          )}
          <StatusLabel variant="confirmation" startIcon={<LineCheckCircleIcon />}>
            {addedLabel}
          </StatusLabel>
        </>
      }
      image={esimImage}
      actions={actionButton}
    >
      <Group direction="vertical" gap={12}>
        <BodyText variant="medium">{description}</BodyText>
        {!isWithinRedemptionWindow && (
          <BodyText variant="medium" colour={rebrandSuccessDarkColour} weight="bold">
            {availabilityNote}
          </BodyText>
        )}
        {canShowUpsellCatalog && upsellCatalog && upsellCountryCode && (
          <LoyaltyEsimAdditionalDataRow
            includedBenefit={includedBenefit}
            countryCode={upsellCountryCode}
            order={order}
            upsell={{
              catalog: upsellCatalog,
              currentPackageTicketId,
            }}
          />
        )}

        {includedBenefit.isHistoricalTier && (
          <LoyaltyTierMessageBanner tier={includedBenefit.tier} density="compact" icon={<LoyaltyTierIcon tier={includedBenefit.tier} size="M" />}>
            {historicalTierLabel}
          </LoyaltyTierMessageBanner>
        )}
      </Group>
    </LoyaltyBenefitItemTile>
  )
}

export default LoyaltyOrderViewBenefitsEsimItem
